import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createFileRoute } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { selectedCouponState } from '~/atoms/selectedCouponState';
import { DiscountForm } from '~/components/common/discount/discount-form/discount-form';
import { DiscountList } from '~/components/common/discount/discount-list/discount-list';
import { RegionSelect } from '~/components/region-select';
import { Button } from '~/components/ui/button';
import { Sidebar, SidebarContent } from '~/components/ui/sidebar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { CouponStatus } from '~/graphql/graphql.gen';
import { withAuthorizationRequired } from '~/libs/auth';
import { AppLoader } from '~/routes/_auth';
import { titleWrapper } from '~/utils/title';
import { discountQueryParamsSchema } from '~/validators/discount-validator';

export const DISCOUNT_SCOPES = [
  'scope.meusnidus.coupons.view',
  'scope.meusnidus.coupons.write',
];

export const Route = createFileRoute('/_auth/_main-navigation/discount/')({
  component: withAuthorizationRequired(RouteComponent, {
    onRedirecting: AppLoader,
    requireScope: DISCOUNT_SCOPES,
  }),
  validateSearch: (search) => discountQueryParamsSchema.parse(search),
});

function RouteComponent() {
  const { t } = useTranslation();
  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();

  const [selectedCoupon, setSelectedCoupon] = useAtom(selectedCouponState);
  const [isOpen, setIsOpen] = useState(false);

  const [tabs] = useState([
    {
      key: 'active',
      title: t('discount_page.tabs.active'),
      component: (
        <DiscountList
          status={CouponStatus.Active}
          noDiscountMessage={t('discount_page.coupons.no_coupon.active')}
          setDiscountModalIsOpen={setIsOpen}
        />
      ),
    },
    {
      key: 'scheduled',
      title: t('discount_page.tabs.scheduled'),
      component: (
        <DiscountList
          status={CouponStatus.Programmated}
          noDiscountMessage={t('discount_page.coupons.no_coupon.scheduled')}
          setDiscountModalIsOpen={setIsOpen}
        />
      ),
    },
    {
      key: 'expired',
      title: t('discount_page.tabs.expired'),
      component: (
        <DiscountList
          status={CouponStatus.Expired}
          noDiscountMessage={t('discount_page.coupons.no_coupon.expired')}
          setDiscountModalIsOpen={setIsOpen}
        />
      ),
    },
    {
      key: 'all',
      title: t('discount_page.tabs.all'),
      component: (
        <DiscountList
          status={CouponStatus.All}
          noDiscountMessage={t('discount_page.coupons.no_coupon.all')}
          setDiscountModalIsOpen={setIsOpen}
        />
      ),
    },
  ]);
  const [currentTab, setCurrentTab] = useState('active');

  useEffect(() => {
    if (selectedCoupon) {
      setIsOpen(true);
    }
  }, [selectedCoupon]);

  const onModalClose = (isOpen: boolean) => {
    setIsOpen(isOpen);
    if (selectedCoupon) setSelectedCoupon(undefined);
  };

  return (
    <>
      <title>{titleWrapper(t('discount_page.url_title'))}</title>
      <section aria-labelledby="primary-heading" className="p-4 sm:p-14">
        <div className="flex items-center justify-between sm:space-x-4">
          <div className="flex w-full flex-col space-y-4 sm:w-auto lg:flex-row lg:items-center lg:space-x-8 lg:space-y-0">
            <h1
              id="primary-heading"
              className="text-4.25xl font-bold text-gray-900"
            >
              {t('discount_page.title')}
            </h1>
            <RegionSelect
              className="w-full lg:w-40"
              value={searchParams.region}
              onValueChange={(value) => {
                navigate({
                  search: (prev) => ({ ...prev, region: value }),
                });
              }}
            />
          </div>
          <div className="fixed bottom-0 left-0 z-10 mb-4 flex w-full items-center px-4 sm:relative sm:mb-0 sm:w-auto sm:px-0">
            <Button
              type="button"
              className="flex w-full grow-0 justify-center"
              onClick={() => setIsOpen(true)}
            >
              {t('discount_page.create_new_code')}
            </Button>
          </div>
        </div>
        <div className="border-b border-gray-100 pb-6 sm:hidden sm:pb-0">
          <label htmlFor="tab-list" className="sr-only">
            {t('sales_page.filter_by')}
          </label>
          <select
            id="tab-list"
            className="form-input mt-6 block w-full rounded border border-gray-500 p-2 text-sm leading-5.5 text-gray-900 outline-none transition duration-300 placeholder:text-gray-500 hover:border-gray-700 focus:border-gray-700 focus:ring-gray-700"
            value={currentTab}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              setCurrentTab(e.target.value)
            }
          >
            {tabs.map((tab) => (
              <option key={tab.key} value={tab.key}>
                {tab.title}
              </option>
            ))}
          </select>
        </div>
        <Tabs
          value={currentTab}
          onValueChange={(value) => setCurrentTab(value)}
        >
          <TabsList className="hidden shrink-0 space-x-8 border-b border-gray-100 py-5 sm:flex">
            {tabs.map((tab) => (
              <TabsTrigger key={tab.key} value={tab.key}>
                {tab.title}
              </TabsTrigger>
            ))}
          </TabsList>

          {tabs.map((tab) => (
            <TabsContent key={tab.key} value={tab.key} className="md:mt-4">
              {tab.component}
            </TabsContent>
          ))}
        </Tabs>
        <Sidebar open={isOpen} onOpenChange={() => onModalClose(false)}>
          <SidebarContent className="lg:w-[50rem]">
            <DiscountForm coupon={selectedCoupon} setIsOpen={onModalClose} />
          </SidebarContent>
        </Sidebar>
      </section>
    </>
  );
}
